import { createStore } from 'zustand/vanilla';

import { onSessionReset } from '@shared/session';

import { TOAST_DURATION_MS, TOAST_MAX_VISIBLE, type ToastKind } from './toast.constant';

export interface ToastItem {
  id: string;
  kind: ToastKind;
  text: string;
  durationMs: number;
}

export interface ToastOptions {
  /** 같은 id 로 다시 부르면 내용을 바꾼다(중복 방지) */
  id?: string;
  durationMs?: number;
}

interface ToastState {
  items: ToastItem[];
}

let sequence = 0;

export const toastStore = createStore<ToastState>()(() => ({ items: [] }));

function push(kind: ToastKind, text: string, options: ToastOptions = {}): string {
  sequence += 1;
  const id = options.id ?? `toast-${sequence}`;
  const item: ToastItem = {
    id,
    kind,
    text,
    durationMs: options.durationMs ?? TOAST_DURATION_MS[kind],
  };
  toastStore.setState((state) => {
    const rest = state.items.filter((existing) => existing.id !== id);
    return { items: [...rest, item].slice(-TOAST_MAX_VISIBLE) };
  });
  return id;
}

function dismiss(id: string): void {
  toastStore.setState((state) => ({ items: state.items.filter((item) => item.id !== id) }));
}

function clear(): void {
  toastStore.setState({ items: [] });
}

/**
 * 토스트 명령형 API. 어느 레이어에서든 부른다. 화면에 그리는 것은 app 이 마운트한 `ToastHost`.
 * 문구는 호출부의 `*.text.ts` 또는 `getErrorMessage(error)` 에서 온다.
 */
export const toast = {
  success: (text: string, options?: ToastOptions) => push('success', text, options),
  error: (text: string, options?: ToastOptions) => push('error', text, options),
  info: (text: string, options?: ToastOptions) => push('info', text, options),
  warning: (text: string, options?: ToastOptions) => push('warning', text, options),
  dismiss,
  clear,
};

onSessionReset(clear);
